import { Injectable, NgZone, OnDestroy, effect, inject } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, finalize, take } from 'rxjs';
import {
  LPR_AXIS_ENTRY_UPDATED_SSE_EVENT,
  LPR_AXIS_IMAGES_READY_SSE_EVENT,
  LPR_LANE_ATTENTION_SSE_EVENT,
  LPR_MODAL_SSE_EVENTS,
  RECONCILIATION_SSE_EVENTS,
  SELECTED_LANE_CHANGED_SSE_EVENT,
  VENDOR_MEMBER_REFUSED_REASON,
} from '../shared/constants/realtime.constants';
import { AuthService } from './auth.service';
import { LocationService } from './location.service';
import { LoggerService } from './logger.service';
import { RoleService } from './role.service';
import { RealtimeEventsService } from './realtime-events.service';
import { LocationApiResponse } from '../shared/types';
import {
  isAttendantFacingLprMessage,
  isUpdatedPusherPayload,
  mergeLprEventPayloadIntoRow,
  resolveLprEntryIdFromNotificationData,
  resolveNotificationTypeFromLprEntry,
  shouldSuppressNewModalForUpdatedPusher,
  syncHeaderDropdownCacheFromLprEntryPutResponse,
  syncNotificationListCacheFromNotificationData,
} from '../notifications/notification.utils';
import { buildCreateOrderRequestFromLanePrepaidRedeemPusher } from '../shared/utils/get-lp-image-prepaid-redeem.util';
import { ToastService, ToastMessage } from './toast.service';
import { getUserDataLaneCameraRoi, getUserDataLaneId } from '../shared/utils/auth-headers.util';
import {
  notificationMatchesSelectedLane,
  resolveNotificationCameraRoi,
  resolveNotificationLaneId,
} from '../shared/utils/gate-lane.util';
import {
  resolveLprNotificationTraceContext,
  resolveLprTraceLicensePlate,
} from '../shared/utils/lpr-notification-trace.util';
import {
  LaneAttentionService,
  MULTI_ELIGIBLE_SELECTION_REASON,
  multiEligibleLaneTitle,
} from './lane-attention.service';
import { ApiService } from './api.service';
import { MultiEligibleSelectionModalStateService } from '../components/multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';
import { ModalState, ModalStateService } from './modal-state.service';

export interface NotificationMessage {
  severity: 'success' | 'info' | 'warn' | 'error';
  summary: string;
  detail?: string;
  life?: number;
  sticky?: boolean;
}

export type { ToastMessage };

export interface NotificationData {
  type:
    | 'fleet-vehicle-found'
    | 'fleet-invoice-added'
    | 'invalid-license-plate'
    | 'order-success'
    | 'create-payment-request'
    | 'create-customer-request'
    | 'create-order-request'
    | 'order-not-found'
    | 'detailing-appointment-found'
    | 'fleet-invoice-added'
    | 'order-completed'
    | 'vehicle-not-found'
    | 'active-subscription'
    | 'paused-subscription'
    | 'exit-active-subscription'
    | 'exit-detailing-appointment-found'
    | 'way-single-wash'
    | 'way-active-subscription'
    | 'way-purchase-selection'
    | 'way-purchase-unredeemable'
    | 'way-subscription-already-redeemed-today'
    | 'multi-eligible-selection'
    | 'admin-extra_cash_added'
    | 'admin-closing_balance_updated'
    | 'entry-not-closed'
    | 'admin-opening_balance_updated'
    | 'closing-missing'
    | 'eod-summary';
  message: string;
  timestamp: Date;
  data?: any;
}

/** Payload from SSE `lpr-axis-images-ready` (Axis plate crop + vehicle URLs). */
export interface LprAxisImagesReadyEvent {
  location_id?: string;
  lpr_entry_id?: string;
  plate?: string;
  plate_image_s3_url?: string;
  image_s3_url?: string;
}

/** Payload from SSE `lpr-axis-entry-updated` when `Updated_event` or `Lost_event` is true (refined plate / metadata). */
export interface LprAxisEntryUpdatedEvent {
  Updated_event?: boolean;
  Lost_event?: boolean;
  location_id?: string;
  lpr_entry_id?: string;
  plate?: string;
  state?: string;
  branch?: string;
  plate_image_s3_url?: string;
  image_s3_url?: string;
  vehicle_info?: { view?: string; type?: string; color?: string };
}

/**
 * Emitted when any channel notification arrives with `updated_pusher: true`.
 * The open modal should update itself in-place when `lpr_entry_id` matches.
 */
export interface LprEntryUpdatedNotification {
  notification: NotificationData;
}

/**
 * Payload from the per-user `selected-lane-changed` event (per-user SSE stream, was Pusher
 * `user-{email}`). Published by the backend after a successful `PATCH /user/me/selected-lane`,
 * delivered only to the SAME user's authenticated streams, so every session of the same user
 * (other tablets/tabs) can sync their header lane + lane-dependent UI.
 */
export interface SelectedLaneChangedEvent {
  user_id?: string | number;
  selected_lane_id?: string | null;
  selected_lane_name?: string | null;
  location_id?: string | null;
}

/**
 * Global NotificationService: manages the authenticated SSE stream, normalizes incoming events
 * into a NotificationData stream, and exposes observables for UI consumers to react to
 * notifications and refreshes.
 * @component
 * @selector app-notification-service
 * @example
 *   <app-notification-service></app-notification-service>
 * @input none - This injectable service has no Angular @Input() properties; it is configured via injected AuthService and LocationService.
 * @output Observable<NotificationData | null> notification$ - Emits the most recent notification payload (or null to clear). Consumers subscribe to display modals or update UI.
 * @output Observable<void> refresh$ - Emits when a refresh of the notifications list is requested (e.g. user action or programmatic trigger).
 *
 * Purpose:
 *   - Establish and manage an SSE connection scoped to the authenticated user.
 *   - Bind LPR, reconciliation, and selected-lane events from the multiplexed SSE stream.
 *   - Convert incoming SSE events into a unified NotificationData object and publish it via notification$.
 *   - Provide a programmatic API to emit notifications, check connection status, reconnect, and trigger notification list refreshes.
 *
 * Key public methods:
 *   - emitNotification(type: NotificationData['type'], data: any, message?: string): void
 *       Emit a notification manually using the same modal/workflow as SSE events. Resolves a default message when not provided.
 *   - getConnectionStatus(): boolean
 *       Return the current SSE connection status (connected/disconnected).
 *   - reconnect(): void
 *       Force a reconnect through RealtimeEventsService (useful for recovery).
 *   - refreshNotifications(): void
 *       Trigger a refresh event via refresh$ (used to refresh notification lists).
 *
 * Important private helpers (behavioral summary):
 *   - initializeSse(): Connects to the authenticated SSE stream and binds LPR events.
 *   - ensureReconciliationSse(): Binds reconciliation and selected-lane events to the same stream.
 *   - handleNotification(type, message, data): Normalizes incoming payloads into NotificationData and emits them via notification$.
 *   - getMessageForEvent(eventName): Provides default human-readable messages for known event types.
 *   - disconnectRealtime(): Safely removes SSE subscriptions and disconnects service-owned streams.
 *
 * Lifecycle:
 *   - On construction, watches AuthService.user() changes and initializes or disconnects SSE when the authenticated user changes (login/logout).
 *   - ngOnDestroy(): Cleans up SSE subscriptions and completes subjects to avoid memory leaks.
 */
/**
 * CWS-1266: refusal cards whose copy the backend writes for the attendant — the
 * expired-membership line, the vendor no-membership line, the answer left on the
 * row after a lookup. Every other LPR event takes the canned per-event text.
 */
const LPR_EVENTS_WITH_BACKEND_COPY: ReadonlySet<string> = new Set([
  'paused-subscription',
  'create-order-request',
  'create-customer-request',
  'invalid-license-plate',
]);

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private sseSub: Subscription | null = null;
  private reconciliationSseSub: Subscription | null = null;
  private ownsSseConnection = false;
  private allowedLocationIds = new Set<string>();
  private locationsFilterReady = false;

  private readonly realtime = inject(RealtimeEventsService);
  private readonly ngZone = inject(NgZone);

  private notificationSubject = new BehaviorSubject<NotificationData | null>(null);
  public notification$ = this.notificationSubject.asObservable();

  private readonly lprAxisImagesReadySubject = new Subject<LprAxisImagesReadyEvent>();
  /** Emits when Axis LPR images are ready (same location channels as other LPR events). */
  readonly lprAxisImagesReady$ = this.lprAxisImagesReadySubject.asObservable();

  private readonly lprAxisEntryUpdatedSubject = new Subject<LprAxisEntryUpdatedEvent>();
  /** Emits when Axis sends a refined or lost LPR row (`Updated_event` / `Lost_event`) for the same `lpr_entry_id`. */
  readonly lprAxisEntryUpdated$ = this.lprAxisEntryUpdatedSubject.asObservable();

  private readonly lprEntryUpdatedNotificationSubject = new Subject<LprEntryUpdatedNotification>();
  /**
   * Emits when a standard channel event arrives with `updated_pusher: true`.
   * Subscribers (notification modal) update the open modal in-place when `lpr_entry_id` matches.
   */
  readonly lprEntryUpdatedNotification$ = this.lprEntryUpdatedNotificationSubject.asObservable();

  private readonly selectedLaneChangedSubject = new Subject<SelectedLaneChangedEvent>();
  /**
   * Emits the cross-session selected-lane change for THIS user (per-user SSE event) when the
   * backend reports this user changed their selected lane on ANY session.
   * User-scoped end-to-end, so a different user's sessions never receive it.
   * The header reconciles the displayed lane + refreshes lane-dependent UI; handlers must
   * be idempotent because the initiating tab also receives its own echo.
   */
  readonly selectedLaneChanged$ = this.selectedLaneChangedSubject.asObservable();

  // Refresh subject for triggering notifications list refresh
  private refreshSubject = new Subject<any>();
  public refresh$ = this.refreshSubject.asObservable();

  /** Skip duplicate deliveries from replayed or repeated SSE events. */
  private readonly recentPusherNotificationKeys = new Map<string, number>();
  private static readonly PUSHER_DEDUPE_MS = 3_000;
  private realtimeInitUserKey: string | null = null;
  private locationChannelSubscribeGeneration = 0;
  private readonly browserOnlineHandler = () => this.handleBrowserOnline();
  private readonly visibilityChangeHandler = () => this.handleVisibilityChange();
  private readonly windowFocusHandler = () => {
    this.ensureRealtimeConnected('window_focus');
  };
  private readonly toast = inject(ToastService);
  private readonly laneAttentionService = inject(LaneAttentionService);
  private readonly apiService = inject(ApiService);
  private readonly multiEligibleModalState = inject(MultiEligibleSelectionModalStateService);
  private readonly modalStateService = inject(ModalStateService);
  /** Dedupes successful / definitive images-ready → GET lpr-entries multi-eligible fallback. */
  private readonly multiEligibleImagesReadyAttempted = new Set<string>();
  /** Prevents concurrent GETs for the same entry while a fallback lookup is in flight. */
  private readonly multiEligibleImagesReadyInFlight = new Set<string>();

  /**
   * Creates the notification service, watches AuthService user changes, and initializes or disconnects realtime accordingly.
   **/
  constructor(
    private authService: AuthService,
    private locationService: LocationService,
    private logger: LoggerService,
    private roleService: RoleService
  ) {
    effect(() => {
      const currentUser = this.authService.user();
      if (currentUser) {
        const userKey = String(currentUser.id ?? currentUser.email ?? '').trim();
        const alreadyReady = userKey && userKey === this.realtimeInitUserKey && !!this.sseSub;
        if (alreadyReady) {
          return;
        }
        this.realtimeInitUserKey = userKey || null;
        this.disconnectRealtime();
        this.initializeRealtime();
      } else {
        this.realtimeInitUserKey = null;
        this.disconnectRealtime();
        // True session teardown (logout): drop the lane-attention stack.
        this.laneAttentionService.clearAll();
      }
    });

    this.modalStateService.modalState$.subscribe(state => this.dismissRefusalToastForPopup(state));

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.browserOnlineHandler);
      window.addEventListener('focus', this.windowFocusHandler);
      document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }

  private initializeRealtime(): void {
    // Attendance-only staff: no LPR / recon / lane SSE from this service.
    // Deploy reload prompts still open the stream via DeploymentNotificationHandlerService.
    const userRole = String(this.authService.user()?.role || '').toLowerCase();
    if (userRole === 'staff' || this.roleService.isStaff()) {
      this.logger.info(
        'Skipping NotificationService realtime for attendance-only staff',
        {},
        'RealtimeSSE'
      );
      return;
    }
    // Cash reconciliation always uses staff SSE (employee-api → /events/stream).
    this.ensureReconciliationSse();
    this.initializeSse();
  }

  private disconnectRealtime(): void {
    this.disconnectReconciliationSse();
    this.disconnectSse();
  }

  /**
   * Cash reconciliation: `realtime.on(type)` for unchanged event names.
   * Envelope channel `reconciliation-{userId}` is metadata only.
   */
  private ensureReconciliationSse(): void {
    if (this.reconciliationSseSub) {
      return;
    }
    if (!this.realtime.isConnected() || this.realtime.getAuthMode() !== 'user') {
      this.realtime.connectUser();
      this.ownsSseConnection = true;
    }
    this.reconciliationSseSub = new Subscription();
    this.bindSseReconciliationEvents(this.reconciliationSseSub);
    // selected-lane-changed is another per-user event on the same always-on user SSE stream.
    this.bindSseSelectedLaneEvent(this.reconciliationSseSub);
    this.logger.info(
      'Cash reconciliation using SSE',
      { events: [...RECONCILIATION_SSE_EVENTS] },
      'ReconciliationSSE'
    );
  }

  private disconnectReconciliationSse(): void {
    this.reconciliationSseSub?.unsubscribe();
    this.reconciliationSseSub = null;
  }

  /**
   * Phase 4: LPR lane events from multiplexed SSE (`create-order-request`, etc.).
   * Attendance-only staff never bind LPR events (no popups / notification center).
   */
  private initializeSse(): void {
    const userRole = String(this.authService.user()?.role || '').toLowerCase();
    if (userRole === 'staff' || this.roleService.isStaff()) {
      this.logger.info('Skipping LPR SSE for attendance-only staff', {}, 'LprSSE');
      return;
    }
    this.resolveAllowedLocations();
    if (!this.realtime.isConnected() || this.realtime.getAuthMode() !== 'user') {
      this.realtime.connectUser();
      this.ownsSseConnection = true;
    }
    this.sseSub?.unsubscribe();
    this.sseSub = new Subscription();
    this.bindSseLprEvents(this.sseSub);
    // Reconciliation already bound in ensureReconciliationSse().
    this.logger.info('LPR notifications using SSE', { transport: 'sse' }, 'LprSSE');
  }

  private disconnectSse(): void {
    this.sseSub?.unsubscribe();
    this.sseSub = null;
    this.allowedLocationIds.clear();
    this.locationsFilterReady = false;
    if (!this.reconciliationSseSub && this.ownsSseConnection) {
      this.realtime.disconnect();
      this.ownsSseConnection = false;
    }
    this.recentPusherNotificationKeys.clear();
    this.notificationSubject.next(null);
  }

  private resolveAllowedLocations(): void {
    const currentUser = this.authService.user();
    this.allowedLocationIds.clear();
    if (currentUser?.location !== null && currentUser?.location !== undefined) {
      this.allowedLocationIds.add(String(currentUser.location));
      this.locationsFilterReady = true;
      return;
    }
    this.locationsFilterReady = false;
    const generation = ++this.locationChannelSubscribeGeneration;
    this.locationService.getLocations().subscribe({
      next: response => {
        if (generation !== this.locationChannelSubscribeGeneration) {
          return;
        }
        this.allowedLocationIds.clear();
        if (response.status && Array.isArray(response.data)) {
          for (const location of response.data as LocationApiResponse[]) {
            if (location.id) {
              this.allowedLocationIds.add(String(location.id));
            }
          }
        }
        this.locationsFilterReady = true;
      },
      error: () => {
        if (generation === this.locationChannelSubscribeGeneration) {
          this.locationsFilterReady = true;
        }
      },
    });
  }

  private isLocationAllowed(data: unknown): boolean {
    if (!data || typeof data !== 'object') {
      return true;
    }
    const row = data as Record<string, unknown>;
    const loc = String(row['location_id'] ?? row['locationId'] ?? '').trim();
    if (!loc) {
      return true;
    }
    if (!this.locationsFilterReady || this.allowedLocationIds.size === 0) {
      return true;
    }
    return this.allowedLocationIds.has(loc);
  }

  /**
   * Plate-only label for SSE console diagnostics (no customer/order/image payload).
   */
  private resolveSseLicensePlate(data: unknown): string {
    return resolveLprTraceLicensePlate(data);
  }

  /** Breadcrumb for LPR SSE receive/filter/emit with lane + redeem correlation fields. */
  private logLprSse(phase: string, eventName: string, data: unknown, detail?: string): void {
    const plate = this.resolveSseLicensePlate(data) || '(no plate)';
    const suffix = detail ? ` — ${detail}` : '';
    const trace = resolveLprNotificationTraceContext(
      data && typeof data === 'object' ? (data as Record<string, unknown>) : undefined,
      { notificationType: eventName, detail: detail ?? undefined }
    );
    const traceLicensePlate =
      typeof trace['licensePlate'] === 'string' ? trace['licensePlate'] : undefined;
    this.logger.trackNotification(`lpr_sse_${phase}`, {
      ...trace,
      licensePlate: plate !== '(no plate)' ? plate : traceLicensePlate,
    });
    this.logger.info(`[LprSSE][${phase}] ${eventName} plate=${plate}${suffix}`, trace, 'LprSSE');
  }

  private bindSseLprEvents(sub: Subscription): void {
    for (const eventName of LPR_MODAL_SSE_EVENTS) {
      sub.add(
        this.realtime.on(eventName).subscribe(data => {
          this.ngZone.run(() => this.dispatchModalLprEvent(eventName, data));
        })
      );
    }

    sub.add(
      this.realtime.on(LPR_LANE_ATTENTION_SSE_EVENT).subscribe(data => {
        this.ngZone.run(() => this.handleLaneAttention(data));
      })
    );

    sub.add(
      this.realtime.on(LPR_AXIS_IMAGES_READY_SSE_EVENT).subscribe(data => {
        this.ngZone.run(() => this.handleLprAxisImagesReady(data));
      })
    );

    sub.add(
      this.realtime.on(LPR_AXIS_ENTRY_UPDATED_SSE_EVENT).subscribe(data => {
        this.ngZone.run(() => this.handleLprAxisEntryUpdated(data));
      })
    );
  }

  private bindSseReconciliationEvents(sub: Subscription): void {
    for (const eventName of RECONCILIATION_SSE_EVENTS) {
      sub.add(
        this.realtime.on(eventName).subscribe(data => {
          this.ngZone.run(() => this.handleReconciliationEvent(eventName, data));
        })
      );
    }
  }

  /** Bind the per-user cross-session selected-lane sync on the user SSE stream. */
  private bindSseSelectedLaneEvent(sub: Subscription): void {
    sub.add(
      this.realtime.on(SELECTED_LANE_CHANGED_SSE_EVENT).subscribe(data => {
        this.ngZone.run(() => this.handleSelectedLaneChanged(data));
      })
    );
  }

  /**
   * Forward a selected-lane change to `selectedLaneChanged$` for the header to apply.
   * Backend per-user SSE routing already guarantees this only reaches THIS user's streams;
   * the `user_id` check is defense-in-depth so a mismatched payload can never hijack the
   * session's lane.
   */
  private handleSelectedLaneChanged(data: unknown): void {
    const event: SelectedLaneChangedEvent =
      data && typeof data === 'object' ? (data as SelectedLaneChangedEvent) : {};
    const currentUserId = this.authService.user()?.id;
    if (
      event.user_id != null &&
      currentUserId != null &&
      String(event.user_id) !== String(currentUserId)
    ) {
      return;
    }
    this.selectedLaneChangedSubject.next(event);
  }

  private dispatchModalLprEvent(eventName: string, data: unknown): void {
    if (!this.isLocationAllowed(data)) {
      // Do not log plate for disallowed locations.
      this.logLprSse('filtered', eventName, null, 'location not allowed');
      return;
    }
    this.logLprSse('received', eventName, data);
    this.dismissLaneAttentionOnRedeem(eventName, data);

    if (eventName === 'redeem-prepaid-purchase') {
      const built = buildCreateOrderRequestFromLanePrepaidRedeemPusher(data);
      this.handleNotification(built.type, built.message, built.data);
      return;
    }

    if (eventName === 'invalid-license-plate' || eventName === 'create-customer-request') {
      const now = new Date().toISOString();
      this.logger.debug(`sse : vehicle detected - ${now}`, data);
    }

    const eventData = (data ?? {}) as Record<string, unknown>;
    let message = this.getMessageForEvent(eventName);
    if (eventName === 'active-subscription') {
      message = 'Subscription found. Please proceed to the wash bay.';
    } else if (eventName === 'way-single-wash' || eventName === 'way-active-subscription') {
      message = 'Recognized at the lane via license plate. Review and confirm the order.';
    } else if (eventName === 'way-purchase-selection') {
      message = 'Select purchase to redeem.';
    } else if (eventName === 'multi-eligible-selection') {
      message =
        eventData['rewash_choice_required'] === true
          ? 'Choose rewash or redeem a purchase.'
          : 'Select package to redeem.';
    } else if (eventName === 'way-purchase-unredeemable') {
      message = 'Way purchase is not redeemable.';
    } else if (eventName === 'way-subscription-already-redeemed-today') {
      message = 'Way subscriptions can be redeemed once in 24 hours.';
    } else if (
      eventName === 'order-success' &&
      (eventData['auto_rewash'] === true || eventData['is_rewash'] === true)
    ) {
      message = 'Rewash started automatically. Please proceed to the wash bay.';
    } else if (LPR_EVENTS_WITH_BACKEND_COPY.has(eventName)) {
      // The refusal says why the lane said no, and the card is the only place the
      // attendant reads it. Without this the canned text above replaces it and the
      // reason is lost — for the card and for the bell row built from it.
      const fromPayload = String(eventData['message'] ?? '').trim();
      if (isAttendantFacingLprMessage(fromPayload, eventName)) {
        message = fromPayload;
      }
    }

    this.handleNotification(eventName as NotificationData['type'], message, data);
  }

  /**
   * CWS-1679: once any tablet redeems a chooser's entry, the redemption event for that entry
   * reaches every tablet at the location; drop its lane card there too, before the lane
   * filter hides the event from tablets on other lanes.
   */
  private dismissLaneAttentionOnRedeem(eventName: string, data: unknown): void {
    if (eventName !== 'active-subscription' && eventName !== 'order-success') {
      return;
    }
    const row = data && typeof data === 'object' ? (data as Record<string, any>) : null;
    const entryId = resolveLprEntryIdFromNotificationData(row);
    if (entryId) {
      this.laneAttentionService.dismissForLprEntry(entryId);
    }
  }

  /** Whether an LPR row belongs to this tablet's selected lane (any lane when none is selected). */
  private matchesSelectedLane(row: Record<string, any> | null | undefined): boolean {
    return notificationMatchesSelectedLane(
      row ?? undefined,
      getUserDataLaneId(),
      getUserDataLaneCameraRoi()
    );
  }

  private handleReconciliationEvent(eventName: string, data: any): void {
    if (eventName === 'admin-extra_cash_added') {
      this.add({
        severity: 'success',
        summary: 'Extra cash added!',
        detail: data?.message,
        sticky: true,
      });
      this.refreshSubject.next({ event: 'admin-extra_cash_added', data });
      return;
    }
    if (eventName === 'admin-closing_balance_updated') {
      this.add({
        severity: 'warn',
        summary: 'Closing balance updated!',
        detail: data?.message,
        sticky: true,
      });
      this.refreshSubject.next({ event: eventName, data });
      return;
    }
    if (eventName === 'entry-not-closed') {
      this.add({
        severity: 'warn',
        summary: 'Entry not closed!',
        detail: data?.message,
        sticky: true,
      });
      this.refreshSubject.next({ event: eventName, data });
      return;
    }
    if (eventName === 'admin-opening_balance_updated') {
      this.add({
        severity: 'success',
        summary: 'Opening balance updated!',
        detail: data?.message,
        sticky: true,
      });
      this.refreshSubject.next({ event: eventName, data });
      return;
    }
    if (eventName === 'eod-summary') {
      if (this.shouldSuppressReconciliationToast(eventName, data)) {
        return;
      }
      this.add({
        severity: 'success',
        summary: 'EOD Summary generated!',
        detail: data?.message,
        sticky: true,
      });
      return;
    }
    if (eventName === 'closing-missing') {
      if (this.shouldSuppressReconciliationToast(eventName, data)) {
        return;
      }
      this.add({
        severity: 'warn',
        summary: 'Closing balance not submitted!',
        detail: data?.message,
        sticky: true,
      });
    }
  }

  private ensureRealtimeConnected(source: string): void {
    if (!this.authService.user()) {
      return;
    }
    if (!this.realtime.isConnected()) {
      this.logger.debug(`SSE reconnect (${source})`, {}, 'RealtimeSSE');
      this.realtime.reconnect();
    }
  }

  private handleVisibilityChange(): void {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
      return;
    }
    this.ensureRealtimeConnected('visibility');
  }

  private handleBrowserOnline(): void {
    this.ensureRealtimeConnected('browser_online');
  }

  /**
   * CWS-1650 Phase 4: the refused-member toast is for attendants on the other lanes. The
   * refusing lane (and anyone with no lane selected) already gets the New Order Request card.
   */
  /**
   * CWS-1730: the scanning user gets the popup, everyone else gets the toast. A header scan
   * names its user (`scanned_by_user_id`); a camera read does not, so the toast is shown
   * wherever its popup is not open or queued (other lanes, an admin off the Orders page).
   */
  private isRefusalToastForMe(row: Record<string, unknown> | null): boolean {
    if (!row) {
      return false;
    }
    if (String(row['scanned_by_user_id'] ?? '').trim()) {
      return this.isScannedByAnotherUser(row);
    }
    return !this.modalStateService.hasNotificationForLprEntry(
      resolveLprEntryIdFromNotificationData(row as Record<string, any>)
    );
  }

  /**
   * CWS-1730: popup or toast, never both. The refusal toast can reach a tablet before the
   * popup for the same entry opens there (it was queued, or its SSE came late); drop it then.
   */
  private dismissRefusalToastForPopup(state: ModalState): void {
    if (!state.isOpen) {
      return;
    }
    this.laneAttentionService.dismissForLprEntry(
      resolveLprEntryIdFromNotificationData(state.notification?.data as Record<string, any>),
      VENDOR_MEMBER_REFUSED_REASON
    );
  }

  /** CWS-1730: true for a header scan made by a different user than the one signed in here. */
  private isScannedByAnotherUser(row: Record<string, unknown>): boolean {
    const scannedBy = String(row['scanned_by_user_id'] ?? '').trim();
    const me = String(this.authService.user()?.id ?? '').trim();
    return !!scannedBy && !!me && scannedBy !== me;
  }

  /**
   * Stackable lane-attention toast from the SSE user stream.
   * Location-filtered; the card itself is intentionally not filtered by header lane.
   * CWS-889: multi_eligible_selection also opens the Choose Membership/Package modal
   * (lane-attention is the event the UI reliably receives for this flow).
   * CWS-1679: the modal opens only on a tablet working that lane (or with no lane selected);
   * tablets on other lanes keep the card and get a warning toast naming the lane.
   */
  private handleLaneAttention(data: unknown): void {
    if (!this.isLocationAllowed(data)) {
      this.logLprSse('filtered', LPR_LANE_ATTENTION_SSE_EVENT, null, 'location not allowed');
      return;
    }
    this.logLprSse('received', LPR_LANE_ATTENTION_SSE_EVENT, data);
    const row = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
    const reason = String(row?.['reason'] ?? '').trim();
    if (reason === VENDOR_MEMBER_REFUSED_REASON && !this.isRefusalToastForMe(row)) {
      this.logLprSse('filtered', LPR_LANE_ATTENTION_SSE_EVENT, data, 'popup shown here');
      return;
    }
    this.laneAttentionService.pushFromPusher(row);

    if (reason !== MULTI_ELIGIBLE_SELECTION_REASON) {
      return;
    }
    const purchaseOptions = row?.['purchase_options'];
    if (!Array.isArray(purchaseOptions) || purchaseOptions.length === 0) {
      this.logger.warn(
        'lane-attention multi_eligible_selection missing purchase_options; modal not opened',
        row,
        'LprSSE'
      );
      return;
    }
    // CWS-1730: another user's header scan opens the chooser on that user's tablet only.
    if (!this.matchesSelectedLane(row) || (row && this.isScannedByAnotherUser(row))) {
      this.logLprSse('filtered', LPR_LANE_ATTENTION_SSE_EVENT, row, 'chooser for another lane');
      this.showWarn(
        multiEligibleLaneTitle(row) ?? 'Another lane: multiple memberships, select one',
        undefined,
        8000
      );
      return;
    }

    const message = String(row?.['message'] ?? '').trim() || 'Select package to redeem.';
    this.handleNotification('multi-eligible-selection', message, {
      ...row,
      event: 'multi-eligible-selection',
      is_lpr_upload: true,
    });
  }

  private handleLprAxisImagesReady(data: any): void {
    if (!this.isLocationAllowed(data)) {
      this.logLprSse('filtered', LPR_AXIS_IMAGES_READY_SSE_EVENT, null, 'location not allowed');
      return;
    }
    this.logLprSse('received', LPR_AXIS_IMAGES_READY_SSE_EVENT, data);

    const location_id = String(data?.location_id ?? data?.locationId ?? '').trim();
    const lpr_entry_id = String(data?.lpr_entry_id ?? data?.lprEntryId ?? '').trim();
    // CWS-889: open chooser from DB row when multi-eligible SSE was missed (common for UI scans).
    this.maybeOpenMultiEligibleFromImagesReady(data, lpr_entry_id);

    const hasPlateKey =
      data != null &&
      (Object.prototype.hasOwnProperty.call(data, 'plate_image_s3_url') ||
        Object.prototype.hasOwnProperty.call(data, 'plateImageS3Url'));
    const hasVehicleKey =
      data != null &&
      (Object.prototype.hasOwnProperty.call(data, 'image_s3_url') ||
        Object.prototype.hasOwnProperty.call(data, 'imageS3Url'));

    if (!hasPlateKey && !hasVehicleKey) {
      return;
    }

    const plate_image_s3_url =
      typeof data?.plate_image_s3_url === 'string'
        ? data.plate_image_s3_url.trim()
        : typeof data?.plateImageS3Url === 'string'
          ? data.plateImageS3Url.trim()
          : '';
    const image_s3_url =
      typeof data?.image_s3_url === 'string'
        ? data.image_s3_url.trim()
        : typeof data?.imageS3Url === 'string'
          ? data.imageS3Url.trim()
          : '';

    const plateRaw = data?.plate ?? data?.vehicle_license_plate ?? data?.vehicleLicensePlate;
    const plate = typeof plateRaw === 'string' ? plateRaw.trim() : '';

    const event: LprAxisImagesReadyEvent = {
      ...(location_id ? { location_id } : {}),
      ...(lpr_entry_id ? { lpr_entry_id } : {}),
      ...(plate ? { plate } : {}),
    };
    if (hasPlateKey) {
      event.plate_image_s3_url = plate_image_s3_url;
    }
    if (hasVehicleKey) {
      event.image_s3_url = image_s3_url;
    }
    this.lprAxisImagesReadySubject.next(event);
  }

  /**
   * When `lpr-axis-images-ready` arrives without a prior multi-eligible modal open, fetch the
   * LPR row. If the backend stored `multi-eligible-selection`, open the chooser from that payload.
   */
  private maybeOpenMultiEligibleFromImagesReady(data: any, lprEntryId: string): void {
    if (!lprEntryId) {
      return;
    }
    if (data?.auto_redeem === true || data?.gate_opened === true) {
      return;
    }
    if (this.multiEligibleImagesReadyAttempted.has(lprEntryId)) {
      return;
    }
    if (this.multiEligibleImagesReadyInFlight.has(lprEntryId)) {
      return;
    }
    this.multiEligibleImagesReadyInFlight.add(lprEntryId);

    this.apiService
      .get(`order/lpr-entries/${lprEntryId}`)
      .pipe(
        take(1),
        finalize(() => {
          this.multiEligibleImagesReadyInFlight.delete(lprEntryId);
        })
      )
      .subscribe({
        next: (response: any) => {
          const entry =
            response?.data?.data && typeof response.data.data === 'object'
              ? response.data.data
              : response?.data;
          if (!entry || typeof entry !== 'object') {
            return;
          }
          const row = mergeLprEventPayloadIntoRow(entry as Record<string, any>);
          const eventType = resolveNotificationTypeFromLprEntry(row);
          if (eventType !== 'multi-eligible-selection') {
            this.rememberMultiEligibleImagesReadyAttempt(lprEntryId);
            return;
          }
          const purchaseOptions = row['purchase_options'];
          if (!Array.isArray(purchaseOptions) || purchaseOptions.length < 2) {
            this.logger.warn(
              'images-ready multi-eligible fallback: row missing purchase_options',
              { lprEntryId, eventType },
              'LprSSE'
            );
            return;
          }
          this.rememberMultiEligibleImagesReadyAttempt(lprEntryId);
          // CWS-1679: the chooser opens only on the lane that scanned the plate.
          if (!this.matchesSelectedLane(row)) {
            return;
          }
          const message = String(row['message'] ?? '').trim() || 'Select package to redeem.';
          const payload = {
            ...row,
            event: 'multi-eligible-selection' as const,
            is_lpr_upload: true,
            lpr_entry_id: lprEntryId,
          };
          const notification: NotificationData = {
            type: 'multi-eligible-selection',
            message,
            timestamp: new Date(),
            data: payload,
          };
          this.logger.trackNotification(
            'images_ready_multi_eligible_fallback',
            resolveLprNotificationTraceContext(notification)
          );
          // Open directly — do not rely on notification$ (dedupe / route / mutex races).
          this.multiEligibleModalState.openModal({
            notification,
            behavior: 'replace',
            shouldStore: true,
          });
          this.emitNotification('multi-eligible-selection', payload, message);
        },
        error: err => {
          this.logger.warn(
            'images-ready multi-eligible fallback: failed to load LPR entry',
            { lprEntryId, err },
            'LprSSE'
          );
        },
      });
  }

  private rememberMultiEligibleImagesReadyAttempt(lprEntryId: string): void {
    this.multiEligibleImagesReadyAttempted.add(lprEntryId);
    if (this.multiEligibleImagesReadyAttempted.size > 40) {
      const first = this.multiEligibleImagesReadyAttempted.values().next().value;
      if (first) {
        this.multiEligibleImagesReadyAttempted.delete(first);
      }
    }
  }

  private handleLprAxisEntryUpdated(data: any): void {
    if (!this.isLocationAllowed(data)) {
      this.logLprSse('filtered', LPR_AXIS_ENTRY_UPDATED_SSE_EVENT, null, 'location not allowed');
      return;
    }
    this.logLprSse('received', LPR_AXIS_ENTRY_UPDATED_SSE_EVENT, data);
    const now = new Date().toISOString();
    this.logger.debug(`realtime : lpr-axis-entry-updated - ${now}`, data);
    const isUpdated =
      data?.Updated_event === true || data?.Updated_event === 'true' || data?.Updated_event === 1;
    const isLost =
      data?.Lost_event === true || data?.Lost_event === 'true' || data?.Lost_event === 1;
    if (!isUpdated && !isLost) {
      return;
    }
    const axisData = data as LprAxisEntryUpdatedEvent;
    this.lprAxisEntryUpdatedSubject.next(axisData);
    const row: Record<string, unknown> = {
      ...axisData,
      id: axisData.lpr_entry_id,
      lpr_entry_id: axisData.lpr_entry_id,
      updated_pusher: true,
    };
    const type = resolveNotificationTypeFromLprEntry(row);
    this.lprEntryUpdatedNotificationSubject.next({
      notification: {
        type,
        message: this.getMessageForEvent(type),
        timestamp: new Date(),
        data: row,
      },
    });
  }

  /**
   * Return a user-friendly message corresponding to the given notification event name.
   * @example
   * getMessageForEvent('order-success')
   * 'Order completed successfully!'
   * @param {{string}} {{eventName}} - The event name identifier to map to a user-facing message.
   * @returns {{string}} A corresponding message string for the provided event, or a default 'Notification received' message if unknown.
   **/
  private getMessageForEvent(eventName: string): string {
    switch (eventName) {
      case 'fleet-vehicle-found':
        return 'Fleet vehicle found!';
      case 'invalid-license-plate':
        return 'Invalid license plate detected';
      case 'order-success':
        return 'Order completed successfully!';
      case 'create-payment-request':
        return 'Payment request created';
      case 'create-customer-request':
        return 'Customer request created';
      case 'create-order-request':
        return 'Order request created';
      case 'order-not-found':
        return 'Order not found';
      case 'order-completed':
        return 'Order completed';
      case 'detailing-appointment-found':
        return 'Detailing appointment found!';
      case 'fleet-invoice-added':
        return 'Fleet invoice added!';
      case 'vehicle-not-found':
        return 'No vehicle found';
      case 'admin-extra_cash_added':
        return 'Extra cash added';
      default:
        return 'Notification received';
    }
  }

  /**
   * Emit a notification object to the service's subject to trigger a modal display.
   * @example
   * handleNotification('info', 'User saved successfully', { id: 1 })
   * undefined
   * @param {{NotificationData['type']}} {{type}} - Notification type (e.g., 'info', 'error', 'warning').
   * @param {{string}} {{message}} - Notification message to display in the modal.
   * @param {{any}} {{data}} - Optional additional data attached to the notification.
   * @returns {{void}} No return value; the function emits the notification via an internal subject.
   **/
  private isRecentDuplicate(key: string): boolean {
    const now = Date.now();
    const lastAt = this.recentPusherNotificationKeys.get(key);
    if (lastAt != null && now - lastAt < NotificationService.PUSHER_DEDUPE_MS) {
      return true;
    }
    this.recentPusherNotificationKeys.set(key, now);
    if (this.recentPusherNotificationKeys.size > 200) {
      for (const [k, at] of this.recentPusherNotificationKeys) {
        if (now - at > NotificationService.PUSHER_DEDUPE_MS) {
          this.recentPusherNotificationKeys.delete(k);
        }
      }
    }
    return false;
  }

  private shouldSuppressReconciliationToast(
    eventName: string,
    data: Record<string, unknown> | undefined | null
  ): boolean {
    const suffix = String(
      data?.['date'] ?? data?.['reconciliation_date'] ?? data?.['location_id'] ?? ''
    ).trim();
    const key = suffix ? `reconciliation:${eventName}:${suffix}` : `reconciliation:${eventName}`;
    return this.isRecentDuplicate(key);
  }

  private isDuplicateMultiEligibleNotification(
    data: Record<string, unknown> | undefined | null
  ): boolean {
    if (!data) {
      return false;
    }
    const entryId = resolveLprEntryIdFromNotificationData(data);
    if (!entryId) {
      return false;
    }
    const openState = this.multiEligibleModalState.getCurrentState();
    if (openState.isOpen) {
      const openEntryId = resolveLprEntryIdFromNotificationData(
        openState.notification?.data as Record<string, unknown> | undefined
      );
      if (openEntryId && String(openEntryId) === String(entryId)) {
        return true;
      }
    }
    return this.isRecentDuplicate(`multi-eligible-selection:entry:${entryId}`);
  }

  private isDuplicatePusherNotification(
    type: NotificationData['type'],
    data: Record<string, unknown> | undefined | null
  ): boolean {
    if (
      type === 'create-order-request' ||
      type === 'create-customer-request' ||
      type === 'create-payment-request' ||
      type === 'invalid-license-plate'
    ) {
      return false;
    }
    if (type === 'multi-eligible-selection') {
      return this.isDuplicateMultiEligibleNotification(data);
    }
    if (!data) {
      return false;
    }
    const entryId = resolveLprEntryIdFromNotificationData(data);
    const plate = String(data['plate'] ?? data['vehicle_license_plate'] ?? '')
      .trim()
      .toUpperCase();
    const locationId = String(data['location_id'] ?? data['locationId'] ?? '').trim();
    const key = entryId
      ? `${type}:entry:${entryId}`
      : plate
        ? `${type}:plate:${plate}:${locationId}`
        : '';
    if (!key) {
      return false;
    }
    return this.isRecentDuplicate(key);
  }

  private handleNotification(type: NotificationData['type'], message: string, data: any): void {
    const selectedLaneId = getUserDataLaneId();
    const selectedCameraRoi = getUserDataLaneCameraRoi();
    const row = (data ?? {}) as Record<string, unknown>;
    // CWS-1730: another user's header scan is that user's popup; this tablet gets the toast.
    if (this.isScannedByAnotherUser(row)) {
      this.logLprSse('filtered', type, data, 'scanned by another user');
      return;
    }
    // UI image scans (`webflow` / is_lpr_upload) are user-initiated — do not drop them.
    // CWS-1679: multi-eligible no longer bypasses the lane: only the scanning lane gets the
    // chooser; other lanes see the lane-attention card (handleLaneAttention).
    const bypassLaneFilter =
      row['is_lpr_upload'] === true ||
      row['isLprUpload'] === true ||
      String(row['image_source'] ?? '').toLowerCase() === 'webflow';
    if (
      !bypassLaneFilter &&
      !notificationMatchesSelectedLane(row, selectedLaneId, selectedCameraRoi)
    ) {
      this.logLprSse('filtered', type, data, 'selected lane mismatch');
      this.logger.trackNotification('lpr_sse_lane_filtered', {
        ...resolveLprNotificationTraceContext(row, { notificationType: type }),
        selectedLaneId: selectedLaneId ?? null,
        selectedCameraRoi: selectedCameraRoi ?? null,
        eventLaneId: resolveNotificationLaneId(row) || null,
        eventRoi: resolveNotificationCameraRoi(row),
      });
      this.logger.debug(`SSE notification filtered for selected lane (${type})`, data);
      return;
    }

    const notification: NotificationData = {
      type,
      message,
      timestamp: new Date(),
      data: { ...data, _lanePusherReceivedAtMs: Date.now() },
    };

    if (isUpdatedPusherPayload(data)) {
      this.logLprSse(
        'updated',
        type,
        data,
        shouldSuppressNewModalForUpdatedPusher(type, row)
          ? 'in-place update (no new popup)'
          : 'in-place update + modal outcome popup'
      );
      this.logger.debug(`SSE lpr-entry-updated-notification (${type})`, data);
      this.lprEntryUpdatedNotificationSubject.next({ notification });
      if (shouldSuppressNewModalForUpdatedPusher(type, row)) {
        return;
      }
    }

    if (this.isDuplicatePusherNotification(type, data as Record<string, unknown>)) {
      this.logLprSse('filtered', type, data, 'duplicate suppressed');
      this.logger.debug(`SSE duplicate notification suppressed (${type})`, data);
      return;
    }

    this.logLprSse('emitted', type, data, 'opening notification stream');
    this.notificationSubject.next(notification);
  }

  /**
   * Emit a notification manually (e.g. from an API response) using the same modal workflow as push events.
   */
  public emitNotification(type: NotificationData['type'], data: any, message?: string): void {
    const resolvedMessage = message || this.getMessageForEvent(type);
    const notification: NotificationData = {
      type,
      message: resolvedMessage,
      timestamp: new Date(),
      data,
    };
    this.notificationSubject.next(notification);
  }

  public getConnectionStatus(): boolean {
    return this.realtime.isConnected();
  }

  public reconnect(): void {
    if (!this.authService.user()) {
      return;
    }
    this.realtime.reconnect();
  }

  /**
   * Trigger a refresh of the notifications list
   */
  public refreshNotifications(data?: any): void {
    this.refreshSubject.next(data);
  }

  /** Silent cache + in-place list patch after PUT order/lpr-entries/:id (any LPR event update). */
  public syncLprEntryFromPutResponse(response: unknown): void {
    const notification = syncHeaderDropdownCacheFromLprEntryPutResponse(response);
    if (!notification) {
      return;
    }
    this.lprEntryUpdatedNotificationSubject.next({ notification });
  }

  /** Patch list/dropdown cache when order-request draft fields are saved locally (before/after PUT). */
  public syncLprEntryFromNotificationData(notification: NotificationData): void {
    syncNotificationListCacheFromNotificationData(notification);
    this.lprEntryUpdatedNotificationSubject.next({ notification });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.browserOnlineHandler);
      window.removeEventListener('focus', this.windowFocusHandler);
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    this.disconnectRealtime();
    // Service teardown: drop the lane-attention stack.
    this.laneAttentionService.clearAll();
    this.notificationSubject.complete();
    this.refreshSubject.complete();
    this.selectedLaneChangedSubject.complete();
  }

  // ---------------------------------------------------------------------------
  // Toast surface, delegated to the leaf ToastService. Replaces PrimeNG's
  // MessageService; method names and payload shape are unchanged so existing
  // call sites keep working. Delegation (rather than owning the toast store here)
  // is what lets AuthService raise toasts without a DI cycle back into this service.
  // ---------------------------------------------------------------------------

  /** PrimeNG-compatible toast entry point. `sticky` toasts do not auto-dismiss. */
  add(toast: ToastMessage): void {
    this.toast.add(toast);
  }

  show(message: NotificationMessage): void {
    this.toast.add(message);
  }

  showSuccess(summary: string, detail?: string, life = 3000): void {
    this.toast.showSuccess(summary, detail, life);
  }

  showInfo(summary: string, detail?: string, life = 3000): void {
    this.toast.showInfo(summary, detail, life);
  }

  showWarn(summary: string, detail?: string, life = 3000): void {
    this.toast.showWarn(summary, detail, life);
  }

  showError(summary: string, detail?: string, life = 3000): void {
    this.toast.showError(summary, detail, life);
  }

  /** Dismiss every toast, or only those raised under `key`. */
  clear(key?: string): void {
    this.toast.clear(key);
  }

  /** Legacy shim: push an LPR entry update straight onto the stream. */
  updateLprEntryNotification(data: LprEntryUpdatedNotification): void {
    this.lprEntryUpdatedNotificationSubject.next(data);
  }

  /** Legacy shim: push a lane change straight onto the stream. */
  updateSelectedLaneChange(data: SelectedLaneChangedEvent): void {
    this.selectedLaneChangedSubject.next(data);
  }
}
